import type { Answers, AnswerValue, PublicDeepDiveTrainingCurriculum, PublicTrainingSection } from './types';
import { Box, CircularProgress, Dialog } from '@mui/material';
import { useApi } from '@shared/services/api';
import confetti from 'canvas-confetti';

import { AnimatePresence, motion } from 'framer-motion';
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { UserContext } from '@/shared/contexts/UserContext';
import { Illustration } from './illustrations';
import InteractiveBlock from './InteractiveBlock';
import QuizQuestion from './QuizQuestion';
import './DeepDiveTrainingModal.scss';

interface DeepDiveTrainingModalProps {
  open: boolean;
  onClose: () => void;
  onComplete: () => void;
}

const sectionVariants = {
  enter: (dir: number) => ({
    opacity: 0,
    x: dir > 0 ? 60 : -60,
    scale: 0.985,
  }),
  center: {
    opacity: 1,
    x: 0,
    scale: 1,
  },
  exit: (dir: number) => ({
    opacity: 0,
    x: dir < 0 ? 60 : -60,
    scale: 0.985,
  }),
};

const headlineVariants = {
  initial: { opacity: 0, y: 14 },
  animate: { opacity: 1, y: 0 },
};

const fireConfetti = () => {
  const fire = (particleRatio: number, opts: confetti.Options) => {
    confetti({
      origin: { y: 0.55 },
      colors: ['#0b74da', '#78bbf1', '#2952a3', '#16a34a', '#a3cdf7'],
      ...opts,
      particleCount: Math.floor(220 * particleRatio),
    });
  };
  fire(0.25, { spread: 26, startVelocity: 55 });
  fire(0.2, { spread: 60 });
  fire(0.35, { spread: 100, decay: 0.91, scalar: 0.85 });
  fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
  fire(0.1, { spread: 120, startVelocity: 45 });
};

const DeepDiveTrainingModal: React.FC<DeepDiveTrainingModalProps> = ({ open, onClose, onComplete }) => {
  const api = useApi();
  const { refetchUser } = useContext(UserContext);

  const [curriculum, setCurriculum] = useState<PublicDeepDiveTrainingCurriculum | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [stepIndex, setStepIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [answers, setAnswers] = useState<Answers>({});
  const [wrongQuestionIds, setWrongQuestionIds] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [checkingAnswer, setCheckingAnswer] = useState(false);
  const [questionFeedback, setQuestionFeedback] = useState<Record<string, string>>({});

  const apiRef = useRef(api);
  apiRef.current = api;

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    (async () => {
      try {
        const result = await apiRef.current.get(`/training/deep-dive/curriculum`);
        if (cancelled) return;
        if (result?.error) {
          setLoadError(result.message || 'Failed to load training');
        } else {
          setCurriculum(result as PublicDeepDiveTrainingCurriculum);
        }
      } catch (err) {
        if (!cancelled) setLoadError('Failed to load training');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) {
      setStepIndex(0);
      setDirection(1);
      setAnswers({});
      setWrongQuestionIds(new Set());
      setCompleted(false);
      setSubmitting(false);
      setCheckingAnswer(false);
      setQuestionFeedback({});
    }
  }, [open]);

  const sections = curriculum?.sections ?? [];
  const totalSteps = sections.length;
  const section: PublicTrainingSection | undefined = sections[stepIndex];
  const isLastSection = stepIndex === totalSteps - 1;

  const currentQuestionId = section?.question?.id;
  const currentAnswer = currentQuestionId ? answers[currentQuestionId] : undefined;
  const currentQuestionWrong = !!(currentQuestionId && wrongQuestionIds.has(currentQuestionId));
  const currentErrorExplanation = currentQuestionWrong
    ? (questionFeedback[currentQuestionId ?? ''] ?? "That's not quite right — review the section above and try again.")
    : undefined;

  const questionNumberLookup = useMemo(() => {
    const lookup: Record<string, number> = {};
    let n = 0;
    for (const s of sections) {
      if (s.question) {
        n += 1;
        lookup[s.question.id] = n;
      }
    }
    return lookup;
  }, [sections]);

  const canAdvance = useMemo(() => {
    if (!section) return false;
    if (!section.question) return true;
    if (currentAnswer === undefined) return false;
    if (Array.isArray(currentAnswer)) return currentAnswer.length > 0;
    return typeof currentAnswer === 'string' && currentAnswer.length > 0;
  }, [section, currentAnswer]);

  const handleAnswerChange = (next: AnswerValue) => {
    if (!currentQuestionId) return;
    setAnswers((prev) => ({ ...prev, [currentQuestionId]: next }));
    if (currentQuestionWrong) {
      setWrongQuestionIds((prev) => {
        const copy = new Set(prev);
        copy.delete(currentQuestionId);
        return copy;
      });
      setQuestionFeedback((prev) => {
        const copy = { ...prev };
        delete copy[currentQuestionId];
        return copy;
      });
    }
  };

  const goNext = () => {
    setDirection(1);
    setStepIndex((i) => Math.min(i + 1, totalSteps - 1));
  };

  const goBack = () => {
    setDirection(-1);
    setStepIndex((i) => Math.max(i - 1, 0));
  };

  // Checks the current section's quiz answer (if any) against the server.
  // Returns true when there's nothing to check or the answer is correct.
  const verifyCurrentAnswer = async (): Promise<boolean> => {
    if (!section?.question || !currentQuestionId) return true;

    setCheckingAnswer(true);
    try {
      const result = await apiRef.current.post(`/training/deep-dive/check-answer`, {
        questionId: currentQuestionId,
        answer: currentAnswer,
      });
      const payload = result as { correct?: boolean; feedback?: string; error?: boolean };

      if (payload?.error || typeof payload?.correct !== 'boolean') {
        setQuestionFeedback((prev) => ({
          ...prev,
          [currentQuestionId]: 'Could not check your answer — please try again.',
        }));
        setWrongQuestionIds((prev) => new Set(prev).add(currentQuestionId));
        return false;
      }

      if (payload.correct) {
        setWrongQuestionIds((prev) => {
          const copy = new Set(prev);
          copy.delete(currentQuestionId);
          return copy;
        });
        return true;
      }

      setQuestionFeedback((prev) => ({
        ...prev,
        [currentQuestionId]: payload.feedback ?? "That's not quite right — review the section above and try again.",
      }));
      setWrongQuestionIds((prev) => new Set(prev).add(currentQuestionId));
      return false;
    } catch {
      setQuestionFeedback((prev) => ({
        ...prev,
        [currentQuestionId]: 'Could not check your answer — please try again.',
      }));
      setWrongQuestionIds((prev) => new Set(prev).add(currentQuestionId));
      return false;
    } finally {
      setCheckingAnswer(false);
    }
  };

  // "Continue": check this section's answer before advancing.
  const handleContinue = async () => {
    if (await verifyCurrentAnswer()) goNext();
  };

  const handleSubmit = async () => {
    // Verify the final section's answer before completing.
    if (!(await verifyCurrentAnswer())) return;

    setSubmitting(true);
    try {
      const result = await apiRef.current.post(`/training/deep-dive/complete`, { answers });
      if (result?.error) {
        setSubmitting(false);
        return;
      }
      const payload = result as { passed: boolean; wrongQuestionIds?: string[] };
      if (payload.passed) {
        setCompleted(true);
        fireConfetti();
        await refetchUser();
        setTimeout(onComplete, 1800);
      } else {
        const wrongIds = new Set(payload.wrongQuestionIds ?? []);
        setWrongQuestionIds(wrongIds);
        const firstWrong = sections.findIndex((s) => s.question && wrongIds.has(s.question.id));
        if (firstWrong >= 0) {
          setDirection(-1);
          setStepIndex(firstWrong);
        }
        setSubmitting(false);
      }
    } catch (err) {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={(_e, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        onClose();
      }}
      maxWidth="lg"
      fullWidth
      classes={{ paper: 'ddt-dialog-paper' }}
      PaperProps={{
        sx: {
          width: 'min(1100px, 96vw)',
          height: 'min(820px, 92vh)',
          margin: { xs: 1, sm: 2 },
        },
      }}
    >
      <Box className="deep-dive-training" role="dialog" aria-label="Deep Dive training">
        <Box className="deep-dive-training__header">
          <span className="deep-dive-training__blob deep-dive-training__blob--a" aria-hidden="true" />
          <span className="deep-dive-training__blob deep-dive-training__blob--b" aria-hidden="true" />
          <span className="deep-dive-training__blob deep-dive-training__blob--c" aria-hidden="true" />
          <span className="deep-dive-training__noise" aria-hidden="true" />

          <div className="deep-dive-training__eyebrow-row">
            <span className="deep-dive-training__eyebrow">Contribution Training</span>
            <div className="deep-dive-training__pill-row">
              {totalSteps > 0 && !completed && (
                <span className="deep-dive-training__step-pill">
                  {stepIndex + 1} / {totalSteps}
                </span>
              )}
              {!completed && !submitting && (
                <button
                  type="button"
                  className="deep-dive-training__cancel"
                  onClick={onClose}
                  aria-label="Cancel training and close"
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                  Cancel
                </button>
              )}
            </div>
          </div>
          <h2 className="deep-dive-training__title">What you can — and can't — share</h2>

          {totalSteps > 0 && !completed && (
            <div
              className="deep-dive-training__progress"
              role="progressbar"
              aria-valuenow={stepIndex + 1}
              aria-valuemin={1}
              aria-valuemax={totalSteps}
            >
              {sections.map((s, i) => {
                const filled = i < stepIndex;
                const active = i === stepIndex;
                return (
                  <span
                    key={s.id}
                    className={[
                      'deep-dive-training__progress-segment',
                      (filled || active) && 'deep-dive-training__progress-segment--filled',
                      active && 'deep-dive-training__progress-segment--active',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  />
                );
              })}
            </div>
          )}
        </Box>

        <Box className="deep-dive-training__body">
          {loading && (
            <Box display="flex" alignItems="center" justifyContent="center" minHeight="320px">
              <CircularProgress sx={{ color: '#3b6fd9' }} />
            </Box>
          )}
          {loadError && (
            <Box color="error.main" textAlign="center" py={6}>
              {loadError}
            </Box>
          )}
          {!loading && !loadError && completed && (
            <motion.div
              className="completion"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            >
              <div className="completion__check-wrap">
                <span className="completion__check-ring" aria-hidden="true" />
                <motion.div
                  className="completion__check"
                  initial={{ scale: 0.4, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: 'spring', stiffness: 220, damping: 15, delay: 0.05 }}
                  aria-hidden="true"
                >
                  <svg
                    width="48"
                    height="48"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M5 12.5l4.5 4.5L19 7" />
                  </svg>
                </motion.div>
              </div>
              <motion.h3
                className="completion__title"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.45, duration: 0.4 }}
              >
                You're cleared to contribute.
              </motion.h3>
              <motion.p
                className="completion__message"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6, duration: 0.4 }}
              >
                Welcome to the network. Continuing into your Deal Retro…
              </motion.p>
            </motion.div>
          )}
          {!loading &&
            !loadError &&
            !completed &&
            section &&
            (() => {
              // Question-only sections (no body, no interactive) get a compact
              // 2-column layout: title on the left, quiz card on the right —
              // instead of stacking title above empty body + illustration above
              // quiz, which leaves a huge vertical gap.
              const isQuestionOnly = !!section.question && !section.body && !section.interactive;

              return (
                <AnimatePresence mode="wait" custom={direction}>
                  <motion.div
                    key={section.id}
                    custom={direction}
                    variants={sectionVariants}
                    initial="enter"
                    animate="center"
                    exit="exit"
                    transition={{ duration: 0.42, ease: [0.16, 1, 0.3, 1] }}
                  >
                    <div
                      className={[
                        'deep-dive-training__section',
                        isQuestionOnly && 'deep-dive-training__section--question-only',
                      ]
                        .filter(Boolean)
                        .join(' ')}
                    >
                      <div className="deep-dive-training__section-intro">
                        <div className="deep-dive-training__section-text">
                          <motion.span
                            className="deep-dive-training__section-eyebrow"
                            variants={headlineVariants}
                            initial="initial"
                            animate="animate"
                            transition={{ duration: 0.4, delay: 0.04 }}
                          >
                            {section.eyebrow ?? `Section ${stepIndex + 1}`}
                          </motion.span>
                          <motion.h3
                            className="deep-dive-training__section-headline"
                            variants={headlineVariants}
                            initial="initial"
                            animate="animate"
                            transition={{ duration: 0.45, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
                          >
                            {section.title}
                          </motion.h3>
                          {section.body && (
                            <motion.p
                              className="deep-dive-training__section-body"
                              variants={headlineVariants}
                              initial="initial"
                              animate="animate"
                              transition={{ duration: 0.45, delay: 0.14 }}
                            >
                              {section.body}
                            </motion.p>
                          )}
                          {section.takeaway && (
                            <motion.div
                              className="deep-dive-training__takeaway"
                              variants={headlineVariants}
                              initial="initial"
                              animate="animate"
                              transition={{ duration: 0.45, delay: 0.2 }}
                            >
                              <span className="deep-dive-training__takeaway-label">Key takeaway</span>
                              <p className="deep-dive-training__takeaway-text">{section.takeaway}</p>
                            </motion.div>
                          )}
                        </div>

                        {isQuestionOnly && section.question ? (
                          <motion.div
                            className="deep-dive-training__quiz-aside"
                            initial={{ opacity: 0, y: 12 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.1, duration: 0.45 }}
                          >
                            <QuizQuestion
                              question={section.question}
                              answer={currentQuestionId ? answers[currentQuestionId] : undefined}
                              onChange={handleAnswerChange}
                              showError={currentQuestionWrong}
                              errorExplanation={currentErrorExplanation}
                              questionNumber={currentQuestionId ? questionNumberLookup[currentQuestionId] : 0}
                            />
                          </motion.div>
                        ) : (
                          <motion.div
                            className="deep-dive-training__illustration-wrap"
                            initial={{ opacity: 0, scale: 0.94 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.05, duration: 0.55, ease: [0.16, 1, 0.3, 1] }}
                          >
                            <span className="deep-dive-training__illustration-glow" aria-hidden="true" />
                            <div className="deep-dive-training__illustration">
                              <Illustration which={section.illustration} />
                            </div>
                          </motion.div>
                        )}
                      </div>

                      {!isQuestionOnly && (section.interactive || section.question) && (
                        <div className="deep-dive-training__section-engage">
                          {section.interactive && (
                            <motion.div
                              className="deep-dive-training__interactive-col"
                              initial={{ opacity: 0, y: 12 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.2, duration: 0.45 }}
                            >
                              <InteractiveBlock block={section.interactive} />
                            </motion.div>
                          )}
                          {section.question && (
                            <div className="deep-dive-training__quiz-col">
                              <QuizQuestion
                                question={section.question}
                                answer={currentQuestionId ? answers[currentQuestionId] : undefined}
                                onChange={handleAnswerChange}
                                showError={currentQuestionWrong}
                                errorExplanation={currentErrorExplanation}
                                questionNumber={currentQuestionId ? questionNumberLookup[currentQuestionId] : 0}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </motion.div>
                </AnimatePresence>
              );
            })()}
        </Box>

        {!completed && !loading && !loadError && (
          <Box className="deep-dive-training__footer">
            <button
              type="button"
              onClick={goBack}
              disabled={stepIndex === 0 || submitting}
              className="deep-dive-training__btn deep-dive-training__btn--ghost"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12,19 5,12 12,5" />
              </svg>
              Back
            </button>
            <span className="deep-dive-training__footer-hint">
              {section?.question ? 'Answer to continue' : 'Take a moment with this'}
            </span>
            {isLastSection ? (
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!canAdvance || submitting || checkingAnswer}
                className="deep-dive-training__btn deep-dive-training__btn--primary"
              >
                {submitting ? 'Submitting…' : checkingAnswer ? 'Checking…' : 'Finish & Submit'}
                {!submitting && !checkingAnswer && (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleContinue}
                disabled={!canAdvance || checkingAnswer}
                className="deep-dive-training__btn deep-dive-training__btn--primary"
              >
                {checkingAnswer ? 'Checking…' : 'Continue'}
                {!checkingAnswer && (
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12,5 19,12 12,19" />
                  </svg>
                )}
              </button>
            )}
          </Box>
        )}
      </Box>
    </Dialog>
  );
};

export default DeepDiveTrainingModal;
